/**
 * Shared presentational components for the Security (Atlas) module.
 * All primitives are sa- prefixed and read tokens from satlas.css.
 */
import React, { useRef, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import FeatherIcon from '../../../components/common/FeatherIcon';
import BrandLoader from '../../../components/common/BrandLoader';
import Breadcrumb from '../../../components/common/Breadcrumb';
import { formatTimeWithTimezone } from '../../../utils/formatters';
import { useChartHover, ChartTooltip } from '../../agent-atlas/components/charts360/hover';
import { formatMetricValue } from '../../../metrics/format';
import { normalize as normalizeSeverity, label as severityLabel } from '../../../severity';
import '../../agent-atlas/components/charts360/charts360.css';

/* Info tooltip — explains what a heading/metric means; `calc` shows the formula.
   Hover or keyboard-focus the ⓘ.
   The popover is position:fixed (placed via JS on open) and PORTALED to <body> so it
   escapes both the app's overflow:hidden scroll containers AND any transformed ancestor
   (e.g. the slide-in panel's translateX, which would otherwise re-anchor position:fixed).
   It's wrapped in a .satlas span so the --sa-* design tokens still resolve. */
export const InfoTip = ({ text, calc }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const HALF = 168; // ~half the max popover width, for viewport clamping
  const place = () => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(r.left + r.width / 2, HALF + 8), window.innerWidth - HALF - 8);
    setPos({ top: r.bottom + 9, left });
  };
  const hide = () => setPos(null);
  useEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos]);
  return (
    <span
      ref={ref}
      className="sa-infotip"
      tabIndex={0}
      role="img"
      aria-label={text}
      onMouseEnter={place}
      onMouseLeave={hide}
      onFocus={place}
      onBlur={hide}
      // The tip often sits inside something clickable (a stat card that drills, a sortable or
      // clickable row, a tab): reading it must not also trigger that.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }}
    >
      <FeatherIcon name="info" size={13} />
      {pos && typeof document !== 'undefined' && createPortal(
        <span className="satlas">
          <span className="sa-infotip-pop sa-infotip-open" role="tooltip" style={{ top: pos.top, left: pos.left }}>
            <span className="sa-infotip-text">{text}</span>
            {calc && <code className="sa-infotip-calc">{calc}</code>}
          </span>
        </span>,
        document.body,
      )}
    </span>
  );
};

/* Hover label — names a compact mark (a surface icon, a "+3") the moment the pointer or
   keyboard focus reaches it. The native title attribute waits about a second and is easy to
   miss, so an icon-only column read as unlabelled. Same portaled, fixed popover as InfoTip, so
   no scroll container clips it. Renders nothing extra when there is no label. */
export const HoverTip = ({ label, children, className = '', ...rest }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const place = () => {
    const el = ref.current;
    if (!el || !label || typeof window === 'undefined') return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(r.left + r.width / 2, 90), window.innerWidth - 90);
    setPos({ top: r.bottom + 8, left });
  };
  const hide = () => setPos(null);
  useEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos]);
  return (
    <span ref={ref} className={className} onMouseEnter={place} onMouseLeave={hide} onFocus={place} onBlur={hide} {...rest}>
      {children}
      {pos && typeof document !== 'undefined' && createPortal(
        <span className="satlas">
          <span className="sa-infotip-pop sa-infotip-open sa-tip-sm" role="tooltip" style={{ top: pos.top, left: pos.left }}>{label}</span>
        </span>,
        document.body,
      )}
    </span>
  );
};

/* Refresh — reloads a view and shows that it did. A reload keeps the rows on screen, so a
   refresh that came back with the same rows looked like a button that did nothing. The icon now
   turns while the request is out (for at least a moment, so a fast one is still seen), the button
   is disabled meanwhile, and the time of the last completed load sits beside it. */
export const RefreshButton = ({ onClick, loading = false, updatedAt = null, label }) => {
  const { t } = useTranslation('securityAtlas');
  const [hold, setHold] = useState(false);
  useEffect(() => {
    if (!hold) return undefined;
    const tm = setTimeout(() => setHold(false), 600);
    return () => clearTimeout(tm);
  }, [hold]);
  const busy = loading || hold;
  const name = label || t('securityAtlas:sharedComponents.refresh', 'Refresh');
  return (
    <span className="sa-refresh">
      {(busy || updatedAt) && (
        <span className="sa-refresh-at" aria-live="polite">
          {busy ? t('securityAtlas:sharedComponents.refreshing')
            : t('securityAtlas:sharedComponents.updatedAt', {
              time: formatTimeWithTimezone(updatedAt),
            })}
        </span>
      )}
      <button type="button" className={`sa-btn sm sa-iconbtn${busy ? ' is-loading' : ''}`} disabled={busy}
        aria-busy={busy} title={name} aria-label={name}
        onClick={() => { setHold(true); onClick(); }}>
        <FeatherIcon name="refresh-cw" size={14} />
      </button>
    </span>
  );
};

/* Measures the container so SVG charts fill the full page width (not a fixed 560). */
function useChartWidth(fallback = 640) {
  const ref = useRef(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0].contentRect.width;
      if (cw > 0) setW(Math.round(cw));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/* --------- layout --------- */
/* Uses the platform's standard page-header markup so headings/taglines match
   the rest of safeui-react. `tip` adds an explanatory ⓘ next to the title.
   `breadcrumbs` (Breadcrumb items) draws the page's trail in its one place, directly above
   the title; a page never places a trail itself (DESIGN.md §4.7). */
export const PageHeader = ({ icon, title, subtitle, tip, actions, breadcrumbs }) => (
  <>
    {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumb items={breadcrumbs} />}
    <div className="main-content-header d-flex flex-row justify-content-between align-items-center mb-4 gap-3">
      <div style={{ minWidth: 0 }}>
        <h1>{icon && <FeatherIcon name={icon} size={24} className="me-2" />}{title}{tip && <InfoTip text={tip} />}</h1>
        {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
      </div>
      {actions && <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end ms-auto" style={{ flex: '0 0 auto' }}>{actions}</div>}
    </div>
  </>
);

export const Card = ({ title, sub, tip, calc, actions, children, bodyStyle, className = '' }) => (
  <div className={`sa-card ${className}`}>
    {(title || actions) && (
      <div className="sa-card-h">
        {title && <h3>{title}{tip && <InfoTip text={tip} calc={calc} />}</h3>}
        {sub && <span className="sa-sub">{sub}</span>}
        {actions && <><div className="sa-sp" />{actions}</>}
      </div>
    )}
    <div className="sa-card-b" style={bodyStyle}>{children}</div>
  </div>
);

/* Icon per tone — the reading's direction, so the line is scannable before it is read. */
const INSIGHT_ICON = {
  crit: 'alert-octagon', high: 'alert-triangle', med: 'alert-circle',
  good: 'check-circle', neutral: 'info',
};

/**
 * One-line takeaway that sits above a chart and says what the chart MEANS —
 * the reading, not a restatement of the axes ("Critical and high carry 62% of the
 * load", not "incidents grouped by severity"). Every caller computes it from the
 * same data the chart draws, so it cannot drift from the bars underneath it.
 *
 * `tone` colours the rail (crit / high / med / good / neutral). Renders nothing when
 * the caller passes no children: a card with no honest reading shows its chart alone
 * rather than a line of filler.
 */
export const Insight = ({ tone = 'neutral', children }) => {
  if (!children) return null;
  return (
    <div className={`sa-insight t-${tone}`}>
      <FeatherIcon name={INSIGHT_ICON[tone] || INSIGHT_ICON.neutral} size={13} className="sa-insight-ic" />
      <span>{children}</span>
    </div>
  );
};

export const Stat = ({ k, v, d, color = 'var(--sa-accent)', icon, tip, calc }) => (
  <div className="sa-stat">
    <div className="sa-stripe" style={{ background: color }} />
    <div className="sa-k">
      {icon && <FeatherIcon name={icon} size={12} className="sa-k-ic" />}
      <span>{k}</span>
      {tip && <InfoTip text={tip} calc={calc} />}
    </div>
    <div className="sa-v">{v}</div>
    {d && <div className="sa-d">{d}</div>}
  </div>
);

/* Table column header with an optional explanatory tooltip.
   Use in place of <th> so the ⓘ sits inline with the column label. */
export const Th = ({ tip, calc, children, ...rest }) => (
  <th {...rest}>
    {tip ? <span className="sa-th-tip">{children}<InfoTip text={tip} calc={calc} /></span> : children}
  </th>
);

/* --------- severity ---------
   Read through the platform scale (src/severity): any spelling (Warning, Informational, 4) is its
   level, and an ungraded value is an em dash, never a guessed level. `good` is this page set's
   own non-severity tone. */
const SEV_MAP = { critical: 'c', high: 'h', medium: 'm', low: 'l', info: 'x', none: 'g' };
const isGood = (s) => String(s || '').toLowerCase() === 'good';

export const sevClass = (s) => (isGood(s) ? 'g' : SEV_MAP[normalizeSeverity(s)] || 'x');

export const Sev = ({ level, children }) => {
  if (isGood(level)) return <span className="sa-sev g">{children || level}</span>;
  const lv = normalizeSeverity(level);
  if (lv === 'unknown') {
    return <span className="sa-sev x" title="Not graded" aria-label="Severity: not graded">—</span>;
  }
  // children that only repeat the raw value show the platform label (warning -> Medium)
  const custom = children != null && String(children).toLowerCase() !== String(level).toLowerCase();
  return <span className={`sa-sev ${SEV_MAP[lv] || 'x'}`}>{custom ? children : severityLabel(lv)}</span>;
};

export const Tag = ({ children }) => <span className="sa-tag">{children}</span>;

/**
 * A titled box inside a slide panel. A panel that is one long scroll of headed lists makes the
 * reader work out where each section ends; boxing them means the eye can skip a whole block.
 *
 * `flush` removes the body padding for children that already draw their own border — the
 * key/value grid, mainly — so the section does not double up on frames.
 */
export const PanelSection = ({ title, meta, flush = false, children }) => (
  <section className="sa-sect">
    <div className="sa-sect-h">
      <span>{title}</span>
      {meta ? <span className="sa-sect-m">{meta}</span> : null}
    </div>
    <div className={`sa-sect-b${flush ? ' flush' : ''}`}>{children}</div>
  </section>
);


/* Evidence/signal flags are FINDINGS, not labels — a secret reaching the output and a note that
   content was retrieved are not the same kind of fact, and rendering both as neutral tags makes
   an analyst read every one to find the one that matters. Tone is explicit for the vocabulary
   the fabric actually emits, with a keyword fallback so a new flag lands somewhere sensible
   rather than silently reading as benign. Shared so the Evidence tab and the Signals panel
   cannot drift apart. */
const FLAG_TONE = {
  secret_in_output: 'crit',
  data_exfiltration: 'crit',
  retrieved_injection: 'crit',
  sensitive_data_in_output: 'warn',
  bulk_access: 'warn',
  external_identity: 'warn',
  unrequested_tool_call: 'warn',
  retrieved_content: 'info',
  agent_acted: 'info',
};

export const flagTone = (f) => {
  const k = String(f || '').toLowerCase();
  if (FLAG_TONE[k]) return FLAG_TONE[k];
  if (/secret|credential|api[_-]?key|private[_-]?key|password|exfil|inject|jailbreak|malware|leak/.test(k)) return 'crit';
  if (/sensitive|pii|bulk|unauthori|external|escalat|unrequested|policy|violation|anomal/.test(k)) return 'warn';
  return 'info';
};

/** Flag/tag text -> readable words ("secret_in_output" -> "Secret In Output"). */
export const humanise = (s) => String(s || '').toLowerCase().replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());


/* --------- field labels (full-record view) --------- */
// The "full record" view must read like evidence, not like a schema dump: an
// analyst should never have to decode `attributes.saf3ai.scan.detection.pi` or
// `gen_ai.agent_id`. This curated dictionary names the common OTEL / enrichment
// / scan keys; everything else falls back to dropping the technical namespace and
// humanising what remains, so a raw dotted key is never surfaced verbatim.
const FIELD_LABELS = {
  'llm.prompt': 'Prompt', 'gen_ai.prompt': 'Prompt', prompt: 'Prompt', input: 'Prompt',
  'llm.response': 'Response', 'gen_ai.response': 'Response', 'gen_ai.completion': 'Response',
  response: 'Response', output: 'Response', message: 'Response',
  'gen_ai.agent_id': 'Agent', 'gen_ai.agent.name': 'Agent', 'gen_ai.agent.display_name': 'Agent',
  'gen_ai.application': 'Agent', agent_id: 'Agent', app: 'Application',
  'gen_ai.conversation.id': 'Conversation', 'gen_ai.session.id': 'Session',
  'gen_ai.request.model': 'Model', 'gen_ai.response.model': 'Model',
  'gen_ai.anthropic.model': 'Model', model: 'Model',
  'gen_ai.source': 'Surface', 'gen_ai.anthropic.surface': 'Surface', source: 'Surface', surface: 'Surface',
  'gen_ai.user.email': 'User', 'gen_ai.user.id': 'User', 'enduser.id': 'User', user: 'User', identity: 'Identity',
  'saf3ai.actor': 'Actor', actor: 'Actor',
  'saf3ai.tenant_id': 'Tenant', org_id: 'Organization', organization_id: 'Organization',
  'saf3ai.scan.threat_severity': 'Threat severity', 'saf3ai.scan.threat_types': 'Threat types',
  'security.threats_found': 'Threats found', 'security.threat_types': 'Threat types',
  'saf3ai.scan.pii_types': 'Sensitive data types', 'saf3ai.scan.pii_entities': 'Sensitive data found',
  'saf3ai.scan.entities_detected': 'Sensitive data found',
  'saf3ai.intent.category': 'Intent category', 'saf3ai.intent.risk_level': 'Intent risk',
  'saf3ai.compliance.frameworks_violated': 'Frameworks violated',
  'saf3ai.behavior.flags': 'Behaviour flags',
  'enrichment.threat_analysis.severity': 'Threat severity',
  'enrichment.threat_analysis.category': 'Threat category',
  'enrichment.threat_analysis.summary': 'Threat summary',
  'gen_ai.ingested_at': 'Ingested', timestamp: 'Time', '@timestamp': 'Time', startTime: 'Time', ts: 'Time',
  trace_id: 'Trace ID', span_id: 'Span ID', traceId: 'Trace ID', spanId: 'Span ID',
  name: 'Operation', kind: 'Type', severity: 'Severity', tags: 'Findings',
};
const FIELD_ACRONYMS = {
  id: 'ID', url: 'URL', uri: 'URI', ip: 'IP', pii: 'PII', llm: 'LLM', ai: 'AI',
  api: 'API', sql: 'SQL', mcp: 'MCP', ttl: 'TTL', dlp: 'DLP', sso: 'SSO', pi: 'PI',
};
const FIELD_NS = /^(attributes|resource|scope|gen_ai|llm|saf3ai|enrichment|security|otel|scan)\./;

/** Internal dot-keyed field name -> human label for the full-record view. Never
 *  exposes the raw key: curated dictionary first, else namespace-stripped humanise. */
export const fieldLabel = (key) => {
  const raw = String(key || '');
  const bare = raw.replace(/^attributes\./, '');
  if (FIELD_LABELS[raw]) return FIELD_LABELS[raw];
  if (FIELD_LABELS[bare]) return FIELD_LABELS[bare];
  let rest = bare;
  while (FIELD_NS.test(rest)) rest = rest.replace(FIELD_NS, '');
  const words = rest.replace(/[._]/g, ' ').trim().split(/\s+/).filter(Boolean)
    .map((w) => FIELD_ACRONYMS[w.toLowerCase()] || (w.charAt(0).toUpperCase() + w.slice(1)));
  return words.join(' ') || bare;
};


/* --------- surfaces --------- */
// The surface key is data: rules match on it (`surface:otel`), incidents store it, and filters
// query it — so it stays as the producer writes it. Only the label changes here.
//
// `otel` in particular reads as plumbing rather than a product: the index carries spans from
// customers' OWN agents, instrumented via OpenTelemetry. "Custom Agents" is what that is to
// the person triaging it; the transport is an implementation detail they should not have to
// translate.
const SURFACE_LABEL = {
  otel: 'Custom Agents',
  gcp: 'Google Cloud',
  // The other two cloud audit feeds. Without these the fallback humanise() renders the
  // surface key literally -- "Aws" -- next to a properly named "Google Cloud" beside it.
  aws: 'Amazon Web Services',
  azure: 'Microsoft Azure',
  sast: 'Code (SAST)',
  mdm: 'Managed Devices',
  gemini_enterprise: 'Gemini Enterprise',
  copilot_studio: 'Copilot Studio',
  copilot: 'Microsoft 365 Copilot',
  gateway: 'AI Gateway',
  endpoint: 'Endpoint',
  // the security plane's name for the Endpoint Security surface (extension, gateways, devices)
  endpoint_security: 'Endpoint Security',
  policy: 'Policy',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  cursor: 'Cursor',
  google_antigravity: 'Google Antigravity',
  deepfake: 'Synthetic Media',
};

/** Surface key -> the name an analyst recognises. Unknown keys fall back to humanise(). */
export const surfaceLabel = (s) => SURFACE_LABEL[String(s || '').toLowerCase()] || humanise(s);


/* --------- states --------- */
/** Card/section loader: the brand shield, no module line (lines are page-level only). */
// `auto`: a page or page body loading gets the large shield and the module's
// fun line; a panel-sized one keeps the medium shield.
export const Loading = () => (
  <div className="sa-spin"><BrandLoader variant="shield" size="md" auto /></div>
);

export const EmptyState = ({ icon = 'inbox', title = 'Nothing to show', message }) => (
  <div className="sa-empty">
    <FeatherIcon name={icon} size={40} />
    <h4>{title}</h4>
    {message && <p>{message}</p>}
  </div>
);

/**
 * Error / unreachable state. Never fabricates data — this is what renders
 * when a backend is down or an endpoint is not yet available.
 */
export const ErrorState = ({ service, onRetry }) => {
  const { t } = useTranslation('securityAtlas');

  return (
    <div className="sa-empty">
      <FeatherIcon name="alert-triangle" size={40} />
      <h4>{t('securityAtlas:sharedComponents.dataUnavailable')}</h4>
      <p>{service ? `The ${service} service did not respond.` : 'The backend did not respond.'} No data is shown rather than placeholder values.</p>
      {onRetry && <button className="sa-btn sm" style={{ marginTop: 12 }} onClick={onRetry}><FeatherIcon name="refresh-cw" size={13} />{t('securityAtlas:sharedComponents.retry')}</button>}
    </div>
  );
};

/**
 * Preview banner — shown on pages whose backend does not exist yet, so the
 * design is visible without silently passing illustrative data off as real.
 */
export const PreviewBanner = ({ children }) => {
  const { t } = useTranslation('securityAtlas');

  return (
    <div className="sa-preview">
      <FeatherIcon name="info" size={15} style={{ flex: 'none', marginTop: 1 }} />
      <div><b>{t('securityAtlas:sharedComponents.designPreview')}</b> {children || 'This surface is not yet wired to a backend. Values are illustrative and will render live once the correlation / detection services ship.'}</div>
    </div>
  );
};

/* --------- charts (inline SVG, no external libs) --------- */
const cvar = (v) => (typeof window !== 'undefined'
  ? getComputedStyle(document.documentElement).getPropertyValue(v).trim() : '');

/* --------- chart hover (the 360 charts' one system, in Atlas tokens) --------- */

/** The tooltip card's theme variables, read from the Atlas tokens on the chart's .satlas root. */
export const SA_TIP_TOKENS = {
  '--s360-card': '--sa-raise',
  '--border-color': '--sa-line',
  '--text-primary': '--sa-t1',
  '--text-secondary': '--sa-t2',
};

/** ChartTooltip (charts360/hover) in Security Atlas tokens. Same props. */
export const SaChartTip = (props) => <ChartTooltip tokens={SA_TIP_TOKENS} className="sa-tip" {...props} />;

/** An engine share (0-100, e.g. a series point's `share`) as display text: "37.5%". Null without
 *  one. Shares are never computed here: the metrics engine sends them. */
export const fmtShare = (share, locale) => formatMetricValue(share, 'pct', locale);

/** Show the card at a focused element (keyboard focus has no pointer position). */
export const showAtEl = (hv, key, el) => {
  if (!el || !el.getBoundingClientRect) { hv.show(key, 0, 0, 'key'); return; }
  const r = el.getBoundingClientRect();
  hv.show(key, r.left + r.width / 2, r.top + Math.min(r.height / 2, 14), 'key');
};

/** Pointer + focus handlers for one hoverable element (a row, a pill, a segment). */
export const hoverProps = (hv, key) => ({
  ...hv.bind(key),
  onFocus: (e) => showAtEl(hv, key, e.currentTarget),
  onBlur: () => hv.hide(),
});

/** The hover root: hides on pointer-leave; the ref anchors the card and carries the tokens. */
export const hoverRoot = (hv) => ({
  ref: hv.rootRef,
  onPointerLeave: (e) => { if (e.pointerType !== 'touch') hv.hide(); },
});

/** Horizontal bars: data = [{label, sub, val, share, color, vc, pick, hint}].
 *  onPick(d, i) makes each row clickable (drill-down); set d.pick=false to opt a row out.
 *  Hovering (or focusing) a row highlights it and shows its value and, when the row carries
 *  one, its share (d.share, 0-100, from the metrics engine; `shareLabel` names it). `valueLabel`
 *  names the value, `hint` (or d.hint) is the card's click line.
 *  tip={false} restores the native title tooltips. */
export const HBars = ({ data, max, fmt = (v) => v, onPick, tip = true, valueLabel, shareLabel, hint }) => {
  const { t, i18n } = useTranslation('securityAtlas');
  const hv = useChartHover({ keys: data.map((_, i) => String(i)) });
  const m = max || Math.max(...data.map((d) => d.val), 1);
  const act = tip && hv.active != null ? data[Number(hv.active)] : null;
  const actClickable = !!act && !!onPick && act.pick !== false;
  return (
    <div className="sa-chart" {...(tip ? hoverRoot(hv) : {})}>
      {data.map((d, i) => {
        const clickable = !!onPick && d.pick !== false;
        const key = String(i);
        return (
          <div
            className={`sa-cbar-row${clickable ? ' click' : ''}${tip && hv.active === key ? ' is-hover' : ''}`}
            key={i}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            title={tip ? undefined : (clickable ? `View ${d.label}` : `${d.label}: ${fmt(d.val)}`)}
            onClick={clickable ? () => onPick(d, i) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(d, i); } } : undefined}
            {...(tip ? hoverProps(hv, key) : {})}
          >
            <div className="lab" title={tip ? undefined : d.label}>{d.label}{d.sub && <small> {d.sub}</small>}</div>
            <div className="sa-cbar-track">
              <i style={{ width: `${Math.max(2, (d.val / m) * 100)}%`, background: d.color }} />
            </div>
            <div className="sa-cbar-val" style={{ color: d.vc || 'var(--sa-t1)' }}>{fmt(d.val)}</div>
          </div>
        );
      })}
      {act && (
        <SaChartTip
          hover={hv}
          title={act.label}
          subtitle={act.sub || undefined}
          rows={[
            { key: 'v', color: act.color, label: valueLabel || t('securityAtlas:sharedComponents.chartValue'), value: fmt(act.val), strong: true },
            ...(act.share != null ? [{ key: 's', label: shareLabel || t('securityAtlas:sharedComponents.chartShare'), value: fmtShare(act.share, i18n.language) }] : []),
          ]}
          note={actClickable ? (act.hint || hint || t('securityAtlas:sharedComponents.chartOpen')) : undefined}
        />
      )}
    </div>
  );
};

/** Stacked horizontal bars: data = [{label, sub, segs:[{k,val,color}], right, pick}].
 *  onPick(d, i) makes each row clickable (drill-down); set d.pick=false to opt a row out. */
export const HStack = ({ data, max, fmt = (v) => v.toLocaleString(), onPick }) => {
  const m = max || Math.max(...data.map((d) => d.segs.reduce((a, s) => a + s.val, 0)), 1);
  return (
    <div className="sa-chart">
      {data.map((d, i) => {
        const clickable = !!onPick && d.pick !== false;
        return (
          <div
            className={`sa-cbar-row${clickable ? ' click' : ''}`}
            key={i}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            title={clickable ? `View ${d.label}` : undefined}
            onClick={clickable ? () => onPick(d, i) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(d, i); } } : undefined}
          >
            <div className="lab" title={d.label}>{d.label}{d.sub && <small> {d.sub}</small>}</div>
            <div className="sa-cbar-seg">
              {d.segs.filter((s) => s.val > 0).map((s, j) => (
                <i key={j} style={{ width: `${(s.val / m) * 100}%`, background: s.color }} title={`${s.k}: ${fmt(s.val)}`} />
              ))}
            </div>
            <div className="sa-cbar-val">{d.right != null ? d.right : fmt(d.segs.reduce((a, s) => a + s.val, 0))}</div>
          </div>
        );
      })}
    </div>
  );
};

/** Stacked columns over time: cats=[], series=[{k,color,vals[]}] */
/** Stacked columns: cats=[], series=[{k,color,vals}].
 *  Opt-in hover (`tip`, or implied by `onPick` / `tipTitle`), in the same system as LineArea: the
 *  column under the pointer is highlighted and a card lists each series' value for it (`tipTitle(i)`
 *  titles it, default cats[i]; `tipRows(i)` adds rows the caller has, e.g. the bucket's total from
 *  the service). onPick(i) makes a column clickable (click, or Enter on the stepped column) and
 *  `pickHint` is the card's click line. With focus, ←/→ step, Home/End jump, Esc hides. Without
 *  any of these the columns keep their native <title> tooltips. */
export const SCol = ({
  cats, series, height = 200, max, tip, onPick, tipTitle, tipRows, pickHint, ariaLabel,
}) => {
  const { t } = useTranslation('securityAtlas');
  const [ref, W] = useChartWidth();
  const interactive = Boolean(tip || onPick || tipTitle);
  const hv = useChartHover({ keys: cats.map((_, i) => String(i)), onActivate: onPick ? (k) => onPick(Number(k)) : undefined });
  const active = interactive && hv.active != null ? Number(hv.active) : null;
  const H = height, pl = 34, pb = 22, pt = 10, pr = 6;
  const iw = W - pl - pr, ih = H - pb - pt;
  const tots = cats.map((_, i) => series.reduce((a, s) => a + s.vals[i], 0));
  const m = max || Math.max(...tots, 1) * 1.15;
  const step = iw / cats.length, bw = Math.min(38, step * 0.62);
  // Thin x-axis labels so a long range (e.g. 90 days) doesn't overlap them: show at
  // most ~ one label per 46px, always including the first and last. Bars still draw
  // for every category — only the labels are decimated.
  const every = Math.max(1, Math.ceil(cats.length / Math.max(2, Math.floor(iw / 46))));
  const showLab = (i) => i === cats.length - 1 || (i % every === 0 && i <= cats.length - 1 - Math.ceil(every / 2));
  const grid = [];
  for (let k = 0; k <= 2; k++) {
    const gv = (m * k) / 2, y = pt + ih - (gv / m) * ih;
    grid.push(<g key={k}><line x1={pl} y1={y} x2={W - pr} y2={y} stroke="var(--sa-line-soft)" /><text className="sa-axlab" x={pl - 6} y={y + 3} textAnchor="end">{Math.round(gv)}</text></g>);
  }
  // The column under the pointer, by x (the plot is preserveAspectRatio="none").
  const nearest = (e) => {
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    const r = svg.getBoundingClientRect();
    const x = r.width > 0 ? ((e.clientX - r.left) * W) / r.width : 0;
    return Math.max(0, Math.min(cats.length - 1, Math.floor((x - pl) / step)));
  };
  // The card sits at the top of the column, not at the pointer.
  const at = (e, i, via) => {
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    const r = svg.getBoundingClientRect();
    const sx = r.width > 0 ? r.width / W : 1;
    const sy = r.height > 0 ? r.height / H : 1;
    hv.show(String(i), r.left + (pl + step * i + step / 2) * sx, r.top + (pt + ih - (tots[i] / m) * ih) * sy, via);
  };
  const move = (e) => { if (e.pointerType !== 'touch') at(e, nearest(e), 'pointer'); };
  const svg = (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img">
      {grid}
      {active != null && (
        <rect x={pl + step * active} y={pt} width={step} height={ih} fill="var(--sa-line-soft)" opacity="0.55" pointerEvents="none" />
      )}
      {cats.map((c, i) => {
        const cx = pl + step * i + step / 2; let yb = pt + ih;
        const dim = active != null && active !== i;
        return (
          <g key={i} opacity={dim ? 0.55 : 1}>
            {series.map((se, si) => { const h = (se.vals[i] / m) * ih; yb -= h; return (
              <rect key={si} data-hk={String(i)} x={cx - bw / 2} y={yb} width={bw} height={Math.max(0, h - 2)} rx="2.5" fill={se.color}
                pointerEvents={interactive ? 'none' : undefined}>
                {!interactive && <title>{c} · {se.k}: {se.vals[i]}</title>}
              </rect>); })}
            {showLab(i) && <text className="sa-axlab" x={cx} y={H - 7} textAnchor="middle">{c}</text>}
          </g>
        );
      })}
      {interactive && (
        <rect className="sa-line-hit" data-testid="sa-col-hit" x={0} y={0} width={W} height={H} fill="transparent"
          onPointerMove={move} onPointerEnter={move}
          onPointerDown={(e) => { if (e.pointerType === 'touch') at(e, nearest(e), 'touch'); }}
          onClick={onPick ? (e) => onPick(active != null ? active : nearest(e)) : undefined} />
      )}
    </svg>
  );
  if (!interactive) return <div className="sa-chart" ref={ref}>{svg}</div>;
  return (
    <div className="sa-chart" ref={ref}>
      <div
        className={`sa-line-hover${onPick ? ' is-pick' : ''}`}
        {...hv.rootProps}
        role="group"
        aria-label={ariaLabel || t('securityAtlas:sharedComponents.chartStepHint')}
      >
        {svg}
      </div>
      {active != null && (
        <SaChartTip
          hover={hv}
          title={tipTitle ? tipTitle(active) : cats[active]}
          rows={[
            ...(tipRows ? tipRows(active) : []),
            ...series.map((se, si) => ({
              key: `s${si}`, color: se.color, kind: 'sq', label: se.k, value: se.vals[active] == null ? '—' : Number(se.vals[active]).toLocaleString(),
            })),
          ]}
          note={onPick ? (pickHint || t('securityAtlas:sharedComponents.chartOpen')) : undefined}
        />
      )}
    </div>
  );
};

/** Line/area: cats=[], vals=[], opt {pct,color,every,max}.
 *  Hover anywhere over the plot snaps to the nearest point: a guide line, the point enlarged and a
 *  card with the value (`tipTitle(i)` titles the card, default cats[i]; `valueLabel` names the
 *  value) and, when `deltas[i]` is given ({delta, previous}, from the metrics engine's time
 *  series), its change from the bucket before: "+301 (from 3)". The chart never computes it.
 *  With focus, ←/→ step, Home/End jump, Esc hides.
 *  Opt-in: onPick(i) makes a point clickable (click, or Enter on the stepped point) and `pickHint`
 *  is the card's click line; onHover(i | null) reports the point under the pointer.
 *  `ariaLabel` names the focusable chart. tip={false} restores the native <title> tooltips. */
export const LineArea = ({
  cats, vals, height = 210, pct = false, color = 'var(--sa-crit)', every = 2, max,
  tip = true, onPick, onHover, tipTitle, valueLabel, pickHint, fmt, ariaLabel, deltas,
}) => {
  const { t } = useTranslation('securityAtlas');
  const [ref, W] = useChartWidth();
  const show = fmt || ((v) => (pct ? `${v}%` : Number(v).toLocaleString()));
  const hv = useChartHover({ keys: vals.map((_, i) => String(i)), onActivate: onPick ? (k) => onPick(Number(k)) : undefined });
  const active = tip && hv.active != null ? Number(hv.active) : null;
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;
  useEffect(() => { if (onHoverRef.current) onHoverRef.current(active); }, [active]);
  const H = height, pl = 32, pb = 22, pt = 14, pr = 12;
  const iw = W - pl - pr, ih = H - pb - pt;
  const m = max || Math.max(...vals, 1) * 1.25;
  const X = (i) => pl + (iw * i) / (cats.length - 1);
  const Y = (v) => pt + ih - (v / m) * ih;
  const grid = [];
  for (let k = 0; k <= 2; k++) { const gv = (m * k) / 2, y = Y(gv); grid.push(<g key={k}><line x1={pl} y1={y} x2={W - pr} y2={y} stroke="var(--sa-line-soft)" /><text className="sa-axlab" x={pl - 6} y={y + 3} textAnchor="end">{pct ? `${gv.toFixed(0)}%` : Math.round(gv)}</text></g>); }
  const dl = vals.map((v, i) => `${i ? 'L' : 'M'}${X(i)},${Y(v)}`).join(' ');
  const area = `M${X(0)},${Y(0)} ${vals.map((v, i) => `L${X(i)},${Y(v)}`).join(' ')} L${X(vals.length - 1)},${Y(0)} Z`;

  // The point nearest the pointer, by x: the plot is preserveAspectRatio="none", so client px
  // map to viewBox units by the rendered width.
  const nearest = (e) => {
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    const r = svg.getBoundingClientRect();
    const x = r.width > 0 ? ((e.clientX - r.left) * W) / r.width : 0;
    const n = vals.length;
    return n < 2 ? 0 : Math.max(0, Math.min(n - 1, Math.round(((x - pl) / iw) * (n - 1))));
  };
  // The card sits at the point, not the pointer, so it doesn't chase the mouse between points.
  const at = (e, i, via) => {
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    const r = svg.getBoundingClientRect();
    const sx = r.width > 0 ? r.width / W : 1;
    const sy = r.height > 0 ? r.height / H : 1;
    hv.show(String(i), r.left + X(i) * sx, r.top + Y(vals[i]) * sy, via);
  };
  const move = (e) => { if (e.pointerType !== 'touch') at(e, nearest(e), 'pointer'); };

  // The change from the bucket before, as the engine sent it (deltas[i] = {delta, previous}).
  const change = (() => {
    const d = active != null && deltas ? deltas[active] : null;
    if (!d || d.delta == null || !Number.isFinite(Number(d.delta))) return null;
    const dv = Number(d.delta);
    const sign = dv > 0 ? '+' : dv < 0 ? '−' : '±';
    const abs = pct ? `${Math.abs(dv).toFixed(0)} pt` : Math.abs(dv).toLocaleString();
    // "+301 (from 3)", not "+301 (+10033%)": a percentage off a tiny base reads as noise.
    const rel = !pct && dv !== 0 && d.previous != null
      ? ` (${t('securityAtlas:sharedComponents.chartFrom', { n: Number(d.previous).toLocaleString() })})` : '';
    return `${sign}${abs}${rel}`;
  })();

  return (
    <div className="sa-chart" ref={ref}>
      <div
        className={`sa-line-hover${onPick ? ' is-pick' : ''}`}
        {...(tip ? hv.rootProps : {})}
        {...(tip ? { role: 'group', 'aria-label': ariaLabel || t('securityAtlas:sharedComponents.chartStepHint') } : {})}
      >
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" role="img">
          {grid}
          <path d={area} fill={color} opacity="0.13" />
          <path d={dl} fill="none" stroke={color} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
          {active != null && (
            <line className="sa-line-guide" x1={X(active)} x2={X(active)} y1={pt} y2={pt + ih}
              stroke="var(--sa-t3)" strokeDasharray="3 3" strokeWidth="1" pointerEvents="none" />
          )}
          {vals.map((v, i) => { const last = i === vals.length - 1; const on = active === i; return (
            <g key={i}>
              <circle data-hk={String(i)} cx={X(i)} cy={Y(v)} r={on ? 6 : last ? 4.5 : 3}
                fill={last || on ? color : 'var(--sa-content)'} stroke={on ? 'var(--sa-content)' : color} strokeWidth="2"
                pointerEvents="none">
                {!tip && <title>{cats[i]}: {pct ? `${v}%` : v}</title>}
              </circle>
              {last && <><rect x={X(i) - 30} y={Y(v) - 26} width="52" height="18" rx="5" fill={color} pointerEvents="none" /><text x={X(i) - 4} y={Y(v) - 13} textAnchor="middle" fontSize="11" fontWeight="700" fill="#12161f" fontFamily="ui-monospace,monospace" pointerEvents="none">{pct ? `${v}%` : v}</text></>}
            </g>); })}
          {cats.map((c, i) => (i % every === 0 || i === cats.length - 1) && <text key={i} className="sa-axlab" x={X(i)} y={H - 7} textAnchor="middle">{c}</text>)}
          {tip && (
            <rect className="sa-line-hit" data-testid="sa-line-hit" x={0} y={0} width={W} height={H} fill="transparent"
              onPointerMove={move} onPointerEnter={move}
              onPointerDown={(e) => { if (e.pointerType === 'touch') at(e, nearest(e), 'touch'); }}
              onClick={onPick ? (e) => onPick(active != null ? active : nearest(e)) : undefined} />
          )}
        </svg>
      </div>
      {active != null && (
        <SaChartTip
          hover={hv}
          title={tipTitle ? tipTitle(active) : cats[active]}
          rows={[
            { key: 'v', color, kind: 'line', label: valueLabel || t('securityAtlas:sharedComponents.chartValue'), value: show(vals[active]), strong: true },
            ...(change != null ? [{ key: 'd', label: t('securityAtlas:sharedComponents.chartChange'), value: change }] : []),
          ]}
          note={onPick ? (pickHint || t('securityAtlas:sharedComponents.chartOpen')) : undefined}
        />
      )}
    </div>
  );
};

/** Target-vs-actual meters: data=[{label,pct,target,disp,state}] */
export const Meters = ({ data }) => {
  const col = (s) => ({ c: 'var(--sa-crit)', h: 'var(--sa-high)', m: 'var(--sa-med)', g: 'var(--sa-good)' }[s] || 'var(--sa-good)');
  return (
    <div className="sa-chart">
      {data.map((d, i) => (
        <div className="sa-cbar-row" key={i} style={{ gridTemplateColumns: '180px 1fr 58px' }}>
          <div className="lab" title={d.label}>{d.label}</div>
          <div className="sa-cbar-track" title={d.label}>
            <i style={{ width: `${Math.min(100, d.pct)}%`, background: col(d.state) }} />
            {d.target != null && <span style={{ position: 'absolute', left: `${d.target}%`, top: -2, bottom: -2, width: 2, background: 'var(--sa-t2)' }} />}
          </div>
          <div className="sa-cbar-val" style={{ color: col(d.state) }}>{d.disp}</div>
        </div>
      ))}
    </div>
  );
};

/** Donut / ring for part-of-whole composition: data=[{label,val,share,color,nav,hint}].
 *  onPick(d) makes arcs + legend rows clickable (drill-down). centerLabel/centerSub
 *  render inside the hole (e.g. a total or a headline %). Hovering an arc or its legend row
 *  (or focusing a clickable row) highlights that arc, dims the others and shows its count and,
 *  when the slice carries one, its share (d.share, 0-100, from the metrics engine; `valueLabel` /
 *  `shareLabel` name them; d.hint or `hint` is the click line). Slice sizes are drawn from the
 *  values. tip={false} restores the native <title> tooltips. */
export const Donut = ({
  data, onPick, size = 172, thickness = 24, centerLabel, centerSub, fmt = (v) => Number(v).toLocaleString(),
  tip = true, valueLabel, shareLabel, hint,
}) => {
  const { t, i18n } = useTranslation('securityAtlas');
  const rows = (data || []).filter((d) => d && d.val != null);
  const hv = useChartHover({ keys: rows.map((_, i) => String(i)) });
  const total = rows.reduce((a, d) => a + (Number(d.val) || 0), 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  const segs = rows.map((d, i) => ({ d, i })).filter(({ d }) => d.val > 0).map(({ d, i }) => {
    const frac = total ? d.val / total : 0;
    const s = { d, i, dash: frac * circ, offset: acc };
    acc += frac * circ;
    return s;
  });
  const clickable = (d) => !!onPick && d.nav != null && d.nav !== false;
  const act = tip && hv.active != null ? rows[Number(hv.active)] : null;
  return (
    <div className={`sa-donut-wrap${tip ? ' is-tip' : ''}`} {...(tip ? hoverRoot(hv) : {})}>
      <div className="sa-donut" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img">
          <g transform={`rotate(-90 ${c} ${c})`}>
            <circle cx={c} cy={c} r={r} fill="none" stroke="var(--sa-line-soft)" strokeWidth={thickness} />
            {segs.map((s) => (
              <circle
                key={s.i} cx={c} cy={c} r={r} fill="none" stroke={s.d.color}
                strokeWidth={tip && hv.active === String(s.i) ? thickness + 4 : thickness}
                strokeDasharray={`${s.dash} ${circ - s.dash}`} strokeDashoffset={-s.offset}
                className={clickable(s.d) ? 'sa-donut-seg click' : 'sa-donut-seg'}
                style={tip ? { opacity: hv.dim(String(s.i)) } : undefined}
                onClick={clickable(s.d) ? () => onPick(s.d) : undefined}
                {...(tip ? hv.bind(String(s.i)) : {})}
              >
                {!tip && <title>{s.d.label}: {fmt(s.d.val)}</title>}
              </circle>
            ))}
          </g>
          {centerLabel != null && <text x={c} y={c - 1} textAnchor="middle" className="sa-donut-c">{centerLabel}</text>}
          {centerSub != null && <text x={c} y={c + 15} textAnchor="middle" className="sa-donut-cs">{centerSub}</text>}
        </svg>
      </div>
      <div className="sa-donut-legend">
        {rows.map((d, i) => (
          <div
            key={i}
            className={`${clickable(d) ? 'sa-donut-li click' : 'sa-donut-li'}${tip && hv.active === String(i) ? ' is-hover' : ''}`}
            role={clickable(d) ? 'button' : undefined}
            tabIndex={clickable(d) ? 0 : undefined}
            onClick={clickable(d) ? () => onPick(d) : undefined}
            onKeyDown={clickable(d) ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(d); } } : undefined}
            {...(tip ? hoverProps(hv, String(i)) : {})}
          >
            <span className="dot" style={{ background: d.color }} />
            <span className="lab" title={tip ? undefined : d.label}>{d.label}</span>
            <span className="val">{fmt(d.val)}</span>
          </div>
        ))}
      </div>
      {act && (
        <SaChartTip
          hover={hv}
          title={act.label}
          rows={[
            { key: 'v', color: act.color, label: valueLabel || t('securityAtlas:sharedComponents.chartValue'), value: fmt(act.val), strong: true },
            ...(act.share != null ? [{ key: 's', label: shareLabel || t('securityAtlas:sharedComponents.chartShare'), value: fmtShare(act.share, i18n.language) }] : []),
          ]}
          note={clickable(act) ? (act.hint || hint || t('securityAtlas:sharedComponents.chartOpen')) : undefined}
        />
      )}
    </div>
  );
};

export { cvar };
